import { FC, useEffect, useMemo } from 'react';

import { getSeasonConfig } from './get-season-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalSemanticColors } from './types';

/**
 * Generates CSS variable overrides from semantic color configuration
 * Uses !important to ensure seasonal colors take precedence over Chakra's light/dark mode
 */
const generateSeasonalCSS = (themeClassName: string, colors: SeasonalSemanticColors): string => {
	const { primary, bg, text, border, gray, white, black } = colors;

	return `
		/* Seasonal Theme - Complete color override when .${themeClassName} is on root */
		/* Uses !important to take precedence over Chakra's light/dark mode */
		.${themeClassName} {
			/* Primary colors */
			--chakra-colors-primary-fg: ${primary.fg} !important;
			--chakra-colors-primary-fgStrong: ${primary.fgStrong} !important;
			--chakra-colors-primary-fgStronger: ${primary.fgStronger} !important;
			--chakra-colors-primary-fgStrongest: ${primary.fgStrongest} !important;
			--chakra-colors-primary-bg: ${primary.bg} !important;
			--chakra-colors-primary-bgHover: ${primary.bgHover} !important;
			--chakra-colors-primary-bgActive: ${primary.bgActive} !important;
			--chakra-colors-primary-bgAlpha: ${primary.bgAlpha} !important;
			--chakra-colors-primary-bgAlphaHover: ${primary.bgAlphaHover} !important;
			--chakra-colors-primary-bgAlphaActive: ${primary.bgAlphaActive} !important;

			/* Background colors */
			--chakra-colors-bg-canvas: ${bg.canvas} !important;
			--chakra-colors-bg-surface: ${bg.surface} !important;
			--chakra-colors-bg-soft: ${bg.soft} !important;
			--chakra-colors-bg-subtle: ${bg.subtle} !important;
			--chakra-colors-bg-topNav: ${bg.topNav} !important;
			--chakra-colors-bg-inset: ${bg.inset} !important;

			/* Text colors */
			--chakra-colors-text-default: ${text.default} !important;
			--chakra-colors-text-soft: ${text.soft} !important;
			--chakra-colors-text-softer: ${text.softer} !important;
			--chakra-colors-text-softest: ${text.softest} !important;
			--chakra-colors-text-onColor: ${text.onColor} !important;
			--chakra-colors-text-interactive: ${text.interactive} !important;

			/* Border colors */
			--chakra-colors-border-default: ${border.default} !important;
			--chakra-colors-border-soft: ${border.soft} !important;

			/* Gray scale */
			--chakra-colors-gray-25: ${gray[25]} !important;
			--chakra-colors-gray-50: ${gray[50]} !important;
			--chakra-colors-gray-100: ${gray[100]} !important;
			--chakra-colors-gray-200: ${gray[200]} !important;
			--chakra-colors-gray-300: ${gray[300]} !important;
			--chakra-colors-gray-400: ${gray[400]} !important;
			--chakra-colors-gray-500: ${gray[500]} !important;
			--chakra-colors-gray-600: ${gray[600]} !important;
			--chakra-colors-gray-700: ${gray[700]} !important;
			--chakra-colors-gray-800: ${gray[800]} !important;
			--chakra-colors-gray-900: ${gray[900]} !important;

			/* Base color overrides */
			--chakra-colors-white: ${white} !important;
			--chakra-colors-black: ${black} !important;
		}

		/* Text color for body - background is handled by BackgroundTint component */
		.${themeClassName} body {
			color: ${text.default} !important;
		}
	`;
};

/**
 * Seasonal Theme Overrides Component
 *
 * When seasonal theme is enabled, this completely overrides all color variables
 * with seasonal-specific values. It doesn't try to manipulate Chakra's light/dark
 * mode - it simply takes full control via CSS !important overrides.
 *
 * Three modes exist:
 * 1. Light mode (Chakra default)
 * 2. Dark mode (Chakra dark)
 * 3. Seasonal mode (complete override, ignores light/dark)
 */
export const SeasonalThemeOverrides: FC = () => {
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const seasonConfig = getSeasonConfig();

	// Pick a random button text on mount
	const buttonText = useMemo(() => {
		const randomIndex = Math.floor(Math.random() * seasonConfig.buttonTexts.length);
		return seasonConfig.buttonTexts[randomIndex];
	}, [seasonConfig.buttonTexts]);

	// Add/remove seasonal theme class on root element
	useEffect(() => {
		const root = document.documentElement;

		if (isSeasonalThemeEnabled) {
			root.classList.add(seasonConfig.themeClassName);
		} else {
			root.classList.remove(seasonConfig.themeClassName);
		}

		return () => {
			root.classList.remove(seasonConfig.themeClassName);
		};
	}, [isSeasonalThemeEnabled, seasonConfig.themeClassName]);

	// Replace "Give recognition" button text with seasonal text
	useEffect(() => {
		if (!isSeasonalThemeEnabled) {
			return;
		}

		let foundButton = false;

		const replaceButtonText = () => {
			const giveBoxActions = document.querySelector('[data-testid="give-box-actions"]');
			if (!giveBoxActions) {
				return false;
			}

			const buttons = giveBoxActions.querySelectorAll('button');
			let replacedAny = false;

			buttons.forEach((button) => {
				const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null);

				let node;
				while ((node = walker.nextNode())) {
					if (node.textContent && node.textContent.trim()) {
						const text = node.textContent.trim();
						if (text.includes('Give recognition') || text.includes('Give Recognition') || text === 'Give recognition') {
							node.textContent = node.textContent.replace(/Give recognition/gi, buttonText);
							replacedAny = true;
						}
					}
				}
			});

			return replacedAny;
		};

		if (replaceButtonText()) {
			foundButton = true;
		}

		let intervalId: number | undefined;
		if (!foundButton) {
			intervalId = window.setInterval(() => {
				if (replaceButtonText()) {
					foundButton = true;
					if (intervalId) {
						clearInterval(intervalId);
					}
				}
			}, 200);

			setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 10000);
		}

		const observer = new MutationObserver(() => {
			replaceButtonText();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
			observer.disconnect();
		};
	}, [isSeasonalThemeEnabled, buttonText]);

	if (!isSeasonalThemeEnabled) {
		return null;
	}

	const { themeClassName, semanticColors } = seasonConfig;

	return <style>{generateSeasonalCSS(themeClassName, semanticColors)}</style>;
};
